import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Calendar, 
  Shield, 
  BarChart3,
  ChevronDown,
  ChevronRight,
  Building2,
  Users,
  Layers,
  Check,
  X,
  Target,
  Play,
  Sparkles,
  TrendingUp,
  Clock,
  UserCheck,
  FileText,
  Zap,
  Eye,
  Timer,
  GitBranch,
  DollarSign,
  Percent,
  Activity,
  XCircle
} from 'lucide-react';

// Import logo
import { LOGO_COLLAPSED_DATA } from '../../assets/logoInline';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime;
          const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// Video Modal Component
const VideoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-4xl bg-slate-900 rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          data-testid="video-modal-close"
        >
          <XCircle className="w-6 h-6" />
        </button>
        <div className="aspect-video bg-slate-800 flex items-center justify-center">
          {/* Placeholder for video - replace with actual video embed */}
          <div className="text-center p-8">
            <div className="w-20 h-20 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
              <Play className="w-10 h-10 text-indigo-400" />
            </div>
            <p className="text-slate-300 text-lg mb-2">Video Coming Soon</p>
            <p className="text-slate-500 text-sm">A 2-minute platform overview will be available here.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hero Section with improved bridge line and metrics
const HeroSection = ({ onVideoOpen }) => {
  const [animateStats, setAnimateStats] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimateStats(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center pt-20 pb-12 overflow-hidden">
      {/* Background glow effects - contained within section */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[60vw] max-w-[600px] h-[60vw] max-h-[600px] bg-indigo-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[50vw] max-w-[500px] h-[50vw] max-h-[500px] bg-cyan-500/15 rounded-full blur-[100px]" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left - Content */}
          <div className="space-y-6 sm:space-y-8">
            {/* "MissionAssist Platform" Pill Logo - 28-32px height */}
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
              <img src={LOGO_COLLAPSED_DATA} alt="MissionAssist" className="h-[30px] w-auto rounded-lg mr-1" />
              <span className="text-lg font-semibold text-slate-100">MissionAssist Platform</span>
            </div>
            
            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.1]">
              The Operating System for Insurance Agencies
            </h1>
            
            {/* Bridge Line */}
            <p className="text-base sm:text-lg md:text-xl text-slate-300/90 font-medium leading-relaxed max-w-xl">
              Run your entire agency — from lead intake to booked appointments to compliance — in one intelligent platform.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-indigo-500 text-white font-medium hover:bg-indigo-400 transition-all shadow-lg shadow-indigo-500/25"
                data-testid="hero-demo-cta"
              >
                Book a Demo
                <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={onVideoOpen}
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white/5 text-white font-medium border border-white/10 hover:bg-white/10 transition-all backdrop-blur-sm"
                data-testid="hero-watch-cta"
              >
                <Play className="w-4 h-4" />
                Watch 2-Minute Overview
              </button>
            </div>

            {/* Micro copy */}
            <p className="text-sm text-slate-500">
              See exactly how MissionAssist fits your agency. 15-minute walkthrough.
            </p>
          </div>
          
          {/* Right - Platform UI Preview with animations */}
          <div className="relative mt-8 lg:mt-0">
            <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/20 via-cyan-500/10 to-purple-500/20 rounded-3xl blur-2xl" />
            
            {/* Glass card with UI preview */}
            <div className="relative glass rounded-2xl p-4 sm:p-6 overflow-hidden">
              <div className="space-y-3 sm:space-y-4">
                {/* Header bar with logo - Dashboard Preview: 28-32px */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src={LOGO_COLLAPSED_DATA} alt="MissionAssist" className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg" />
                    <span className="text-xs sm:text-sm font-medium text-white">MissionAssist Dashboard</span>
                  </div>
                  <div className="flex gap-1.5 sm:gap-2">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400/60" />
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400/60" />
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-400/60" />
                  </div>
                </div>
                
                {/* Stats row - responsive grid */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {[
                    { label: 'Leads Today', value: 47, trend: '+12%' },
                    { label: 'Booked', value: 12, trend: '+8%' },
                    { label: 'Conversion', value: 25.5, trend: '+3%', suffix: '%' },
                  ].map((stat, i) => (
                    <div 
                      key={i} 
                      className={`p-2 sm:p-3 rounded-lg bg-white/5 border border-white/5 transition-all duration-500 ${animateStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                      style={{ transitionDelay: `${i * 100}ms` }}
                    >
                      <div className="text-[10px] sm:text-xs text-slate-400 truncate">{stat.label}</div>
                      <div className="flex items-baseline gap-1 sm:gap-2 mt-0.5 sm:mt-1">
                        <span className="text-sm sm:text-lg font-semibold text-white">
                          {animateStats ? <AnimatedCounter end={stat.value} suffix={stat.suffix || ''} /> : '0'}
                        </span>
                        <span className="text-[10px] sm:text-xs text-emerald-400 hidden sm:inline">{stat.trend}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Recent Leads table */}
                <div className="rounded-lg bg-white/5 border border-white/5 overflow-hidden">
                  <div className="px-3 sm:px-4 py-2 border-b border-white/5 flex items-center justify-between">
                    <span className="text-[10px] sm:text-xs font-medium text-slate-400">Recent Leads</span>
                    <span className="text-[10px] sm:text-xs text-indigo-400">View All</span>
                  </div>
                  {[
                    { name: 'Sarah Johnson', source: 'Facebook Ad', status: 'Booked', statusColor: 'emerald' },
                    { name: 'Michael Chen', source: 'Website', status: 'New Lead', statusColor: 'blue' },
                    { name: 'Emily Davis', source: 'Referral', status: 'Contacted', statusColor: 'amber' },
                  ].map((lead, i) => (
                    <div 
                      key={i} 
                      className={`px-3 sm:px-4 py-2 sm:py-3 border-b border-white/5 last:border-0 flex items-center justify-between transition-all duration-500 ${animateStats ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                      style={{ transitionDelay: `${300 + i * 150}ms` }}
                    >
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/30 flex items-center justify-center text-[10px] sm:text-xs font-medium text-white flex-shrink-0">
                          {lead.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs sm:text-sm text-white truncate">{lead.name}</div>
                          <div className="text-[10px] sm:text-xs text-slate-500 truncate">{lead.source}</div>
                        </div>
                      </div>
                      <div className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs flex-shrink-0 ${
                        lead.statusColor === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
                        lead.statusColor === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>
                        {lead.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Metrics Strip */}
        <div className="mt-12 sm:mt-16 pt-8 border-t border-white/5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto">
            {[
              { icon: Zap, label: '2× Faster Lead Response', color: 'indigo' },
              { icon: Percent, label: '30% Higher Appointment Rate', color: 'cyan' },
              { icon: Eye, label: '100% Lead Visibility', color: 'emerald' },
            ].map((metric, i) => (
              <div key={i} className="flex items-center justify-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  metric.color === 'indigo' ? 'bg-indigo-500/20' :
                  metric.color === 'cyan' ? 'bg-cyan-500/20' :
                  'bg-emerald-500/20'
                }`}>
                  <metric.icon className={`w-5 h-5 ${
                    metric.color === 'indigo' ? 'text-indigo-400' :
                    metric.color === 'cyan' ? 'text-cyan-400' :
                    'text-emerald-400'
                  }`} />
                </div>
                <span className="text-sm font-medium text-slate-200">{metric.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-slate-400">
          <span className="text-xs">Scroll to explore</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// Social Proof Section
const SocialProofSection = () => {
  const testimonials = [
    {
      quote: "MissionAssist completely transformed how we handle leads. Our booking rate went up 40% in the first month.",
      name: "James Rodriguez",
      title: "Agency Owner",
      company: "Rodriguez Insurance Group"
    },
    {
      quote: "Finally, a platform built for how insurance agencies actually work. The compliance capture alone saves us hours every week.",
      name: "Sarah Mitchell",
      title: "Operations Manager",
      company: "Midwest Family Insurance"
    },
    {
      quote: "The visibility into our funnel performance is incredible. We can actually see what's producing business now.",
      name: "David Chen",
      title: "Managing Partner",
      company: "Pacific Coast Insurance"
    }
  ];

  return (
    <section className="relative py-16 sm:py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-3 sm:mb-4">
            Trusted by Insurance Agencies
          </h2>
          <p className="text-sm sm:text-base text-slate-400">See what agency owners are saying about MissionAssist</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {testimonials.map((testimonial, i) => (
            <div key={i} className="glass rounded-xl p-5 sm:p-6 hover:bg-white/[0.08] transition-all">
              <div className="flex items-center gap-1 mb-3 sm:mb-4">
                {[...Array(5)].map((_, j) => (
                  <Sparkles key={j} className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400" />
                ))}
              </div>
              <p className="text-slate-300/90 text-sm leading-relaxed mb-4 sm:mb-6">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/30 flex items-center justify-center text-xs sm:text-sm font-medium text-white">
                  {testimonial.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{testimonial.name}</div>
                  <div className="text-xs text-slate-500">{testimonial.title}, {testimonial.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Comparison Table Section
const ComparisonSection = () => {
  const comparisons = [
    { feature: 'Lead Tracking', generic: 'Fragmented across tools', mission: 'Unified in one platform' },
    { feature: 'Appointment Workflow', generic: 'Manual processes', mission: 'Fully automated' },
    { feature: 'Compliance Visibility', generic: 'Spreadsheets & chaos', mission: 'Built-in & automatic' },
    { feature: 'Performance Insights', generic: 'Limited reports', mission: 'AI-guided analytics' },
    { feature: 'Lead Attribution', generic: 'Guesswork', mission: 'Full source tracking' },
  ];

  return (
    <section className="relative py-16 sm:py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-0 w-[50vw] max-w-[500px] h-[50vw] max-h-[500px] bg-red-500/5 rounded-full blur-[100px] -translate-x-1/4" />
        <div className="absolute top-1/2 right-0 w-[50vw] max-w-[500px] h-[50vw] max-h-[500px] bg-emerald-500/5 rounded-full blur-[100px] translate-x-1/4" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4 sm:mb-6">
            Stop Losing Leads to Broken Processes
          </h2>
          <p className="text-base sm:text-lg text-slate-400">
            See how MissionAssist compares to piecing together generic tools.
          </p>
        </div>
        
        {/* Comparison Table */}
        <div className="glass rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-white/5 border-b border-white/10">
            <div className="text-xs sm:text-sm font-semibold text-slate-300">Feature</div>
            <div className="text-xs sm:text-sm font-semibold text-red-400 text-center">Generic Tools</div>
            <div className="text-xs sm:text-sm font-semibold text-emerald-400 text-center flex items-center justify-center gap-2">
              <img src={LOGO_COLLAPSED_DATA} alt="MissionAssist" className="h-8 w-8 rounded-lg" />
              <span className="hidden sm:inline">MissionAssist</span>
            </div>
          </div>
          
          {/* Rows */}
          {comparisons.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 ${i < comparisons.length - 1 ? 'border-b border-white/5' : ''}`}>
              <div className="text-xs sm:text-sm text-white font-medium">{row.feature}</div>
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <X className="w-3 h-3 sm:w-4 sm:h-4 text-red-400 flex-shrink-0" />
                <span className="text-xs sm:text-sm text-slate-400 text-center">{row.generic}</span>
              </div>
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-xs sm:text-sm text-slate-200 text-center">{row.mission}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Assist Engines Section - Powered by Assist Engines
const AssistEnginesSection = () => {
  const engines = [
    { 
      name: 'ExecutiveAssist', 
      description: 'Strategy and decision intelligence',
      icon: TrendingUp,
      color: 'indigo'
    },
    { 
      name: 'FunnelAssist', 
      description: 'Conversion infrastructure',
      icon: GitBranch,
      color: 'cyan'
    },
    { 
      name: 'ScriptAssist', 
      description: 'Messaging and persuasion intelligence',
      icon: FileText,
      color: 'purple'
    },
    { 
      name: 'CampaignAssist', 
      description: 'Advertising and creative intelligence',
      icon: Target,
      color: 'amber'
    },
    { 
      name: 'AnalyticsAssist', 
      description: 'Performance intelligence',
      icon: BarChart3,
      color: 'emerald'
    },
    { 
      name: 'ProfitAssist', 
      description: 'Capital allocation intelligence',
      icon: DollarSign,
      color: 'green'
    },
    { 
      name: 'OperationsAssist', 
      description: 'Execute approved operational actions across connected systems',
      icon: Zap,
      color: 'rose'
    },
  ];

  return (
    <section className="relative py-20 sm:py-32 overflow-hidden" data-testid="assist-engines-section">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] max-w-[900px] h-[50vw] max-h-[500px] bg-indigo-500/10 rounded-full blur-[120px]" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 mb-6">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            <span className="text-sm font-medium text-slate-200">Intelligence Layer</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4 sm:mb-6">
            Powered by Assist Engines
          </h2>
          <p className="text-base sm:text-lg text-slate-300/80">
            Seven specialized AI engines that transform data into decisions and actions.
          </p>
        </div>
        
        {/* Engine Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {engines.map((engine, i) => (
            <div 
              key={i} 
              className="glass rounded-xl p-5 sm:p-6 hover:bg-white/[0.08] transition-all group"
              data-testid={`assist-engine-${engine.name.toLowerCase()}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform ${
                engine.color === 'indigo' ? 'bg-indigo-500/20' :
                engine.color === 'cyan' ? 'bg-cyan-500/20' :
                engine.color === 'purple' ? 'bg-purple-500/20' :
                engine.color === 'amber' ? 'bg-amber-500/20' :
                engine.color === 'emerald' ? 'bg-emerald-500/20' :
                engine.color === 'green' ? 'bg-green-500/20' :
                'bg-rose-500/20'
              }`}>
                <engine.icon className={`w-6 h-6 ${
                  engine.color === 'indigo' ? 'text-indigo-400' :
                  engine.color === 'cyan' ? 'text-cyan-400' :
                  engine.color === 'purple' ? 'text-purple-400' :
                  engine.color === 'amber' ? 'text-amber-400' :
                  engine.color === 'emerald' ? 'text-emerald-400' :
                  engine.color === 'green' ? 'text-green-400' :
                  'text-rose-400'
                }`} strokeWidth={1.5} />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">{engine.name}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{engine.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Platform Overview Section
const PlatformOverviewSection = () => {
  const pillars = [
    { icon: Target, label: 'Funnels', description: 'Capture and qualify leads from any source', color: 'indigo' },
    { icon: Users, label: 'Leads', description: 'Centralized lead management with attribution', color: 'blue' },
    { icon: Calendar, label: 'Appointments', description: 'Automated booking workflows', color: 'cyan' },
    { icon: Shield, label: 'Compliance', description: 'Automatic consent and audit trails', color: 'emerald' },
    { icon: Sparkles, label: 'AI Guidance', description: 'Scripts and real-time assistance', color: 'purple' },
  ];

  return (
    <section id="platform-overview" className="relative py-20 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] max-w-[800px] h-[40vw] max-h-[400px] bg-indigo-500/10 rounded-full blur-[100px]" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-4 sm:mb-6">
            <Layers className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-medium text-slate-200">Platform Overview</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4 sm:mb-6">
            The Agency Operating System
          </h2>
          <p className="text-base sm:text-lg text-slate-300/80">
            Five integrated pillars that power your entire agency operation.
          </p>
        </div>
        
        <div className="relative">
          <div className="hidden lg:block absolute top-[60px] left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-4">
            {pillars.map((pillar, i) => (
              <div key={i} className="relative group">
                <div className="glass rounded-xl p-4 sm:p-6 text-center hover:bg-white/[0.08] transition-all h-full flex flex-col">
                  <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform ${
                    pillar.color === 'indigo' ? 'bg-indigo-500/20' :
                    pillar.color === 'blue' ? 'bg-blue-500/20' :
                    pillar.color === 'cyan' ? 'bg-cyan-500/20' :
                    pillar.color === 'emerald' ? 'bg-emerald-500/20' :
                    'bg-purple-500/20'
                  }`}>
                    <pillar.icon className={`w-5 h-5 sm:w-7 sm:h-7 ${
                      pillar.color === 'indigo' ? 'text-indigo-400' :
                      pillar.color === 'blue' ? 'text-blue-400' :
                      pillar.color === 'cyan' ? 'text-cyan-400' :
                      pillar.color === 'emerald' ? 'text-emerald-400' :
                      'text-purple-400'
                    }`} strokeWidth={1.5} />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-white mb-1 sm:mb-2">{pillar.label}</span>
                  <span className="text-[10px] sm:text-xs text-slate-400 leading-relaxed">{pillar.description}</span>
                </div>
                
                {i < pillars.length - 1 && (
                  <div className="hidden lg:block absolute top-[60px] -right-2 -translate-y-1/2 z-10">
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Product Screenshots Section with improved titles
const ProductScreenshotSection = () => (
  <section className="relative py-20 sm:py-32 overflow-hidden">
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[1000px] h-[50vw] max-h-[600px] bg-indigo-500/10 rounded-full blur-[120px]" />
    </div>
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4 sm:mb-6">
          How MissionAssist Runs Your Agency
        </h2>
        <p className="text-base sm:text-lg text-slate-300/80">
          See exactly what MissionAssist looks like in action.
        </p>
      </div>
      
      <div className="space-y-6 sm:space-y-8">
        {/* Lead Pipeline View */}
        <div className="glass rounded-xl sm:rounded-2xl p-4 sm:p-8 overflow-hidden">
          <div className="text-xs sm:text-sm font-medium text-indigo-400 mb-4 sm:mb-6 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
            Lead Pipeline Intelligence
          </div>
          <div className="rounded-lg sm:rounded-xl bg-white/5 border border-white/10 p-3 sm:p-6 overflow-x-auto">
            <div className="grid grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6 min-w-[280px]">
              {[
                { label: 'New Leads', value: '127', color: 'blue' },
                { label: 'Contacted', value: '89', color: 'amber' },
                { label: 'Booked', value: '54', color: 'cyan' },
                { label: 'Closed', value: '23', color: 'emerald' },
              ].map((stat, i) => (
                <div key={i} className={`p-2 sm:p-4 rounded-lg border ${
                  stat.color === 'blue' ? 'bg-blue-500/10 border-blue-500/20' :
                  stat.color === 'amber' ? 'bg-amber-500/10 border-amber-500/20' :
                  stat.color === 'cyan' ? 'bg-cyan-500/10 border-cyan-500/20' :
                  'bg-emerald-500/10 border-emerald-500/20'
                }`}>
                  <div className="text-[10px] sm:text-xs text-slate-400">{stat.label}</div>
                  <div className={`text-lg sm:text-2xl font-semibold mt-0.5 sm:mt-1 ${
                    stat.color === 'blue' ? 'text-blue-400' :
                    stat.color === 'amber' ? 'text-amber-400' :
                    stat.color === 'cyan' ? 'text-cyan-400' :
                    'text-emerald-400'
                  }`}>{stat.value}</div>
                </div>
              ))}
            </div>
            <div className="h-20 sm:h-32 flex items-end justify-between gap-1 sm:gap-0 sm:px-4">
              {[40, 55, 35, 70, 45, 85, 60, 90, 50, 75, 65, 80].map((h, i) => (
                <div key={i} className="flex-1 sm:w-6 max-w-6 bg-gradient-to-t from-indigo-500/40 to-indigo-400/20 rounded-t" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          {/* Booking Calendar View */}
          <div className="glass rounded-xl sm:rounded-2xl p-4 sm:p-6 overflow-hidden">
            <div className="text-xs sm:text-sm font-medium text-cyan-400 mb-3 sm:mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
              Appointment Booking System
            </div>
            <div className="rounded-lg sm:rounded-xl bg-white/5 border border-white/10 p-3 sm:p-4">
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-4">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                  <div key={i} className="text-[10px] sm:text-xs text-slate-500 text-center">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {[...Array(35)].map((_, i) => {
                  const hasEvent = [3, 7, 8, 12, 15, 19, 22, 26, 28].includes(i);
                  return (
                    <div key={i} className={`aspect-square rounded flex items-center justify-center text-[10px] sm:text-xs ${
                      hasEvent ? 'bg-cyan-500/30 text-cyan-300' : 'bg-white/5 text-slate-500'
                    }`}>
                      {(i % 31) + 1}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Compliance View */}
          <div className="glass rounded-xl sm:rounded-2xl p-4 sm:p-6 overflow-hidden">
            <div className="text-xs sm:text-sm font-medium text-emerald-400 mb-3 sm:mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
              Compliance Visibility
            </div>
            <div className="rounded-lg sm:rounded-xl bg-white/5 border border-white/10 p-2 sm:p-4 space-y-2 sm:space-y-3">
              {[
                { label: 'TCPA Consent', status: 'Captured', icon: Check },
                { label: 'Call Recording', status: 'Stored', icon: Check },
                { label: 'Disclosure', status: 'Complete', icon: Check },
                { label: 'Audit Trail', status: 'Active', icon: FileText },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <item.icon className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" />
                    </div>
                    <span className="text-xs sm:text-sm text-slate-300">{item.label}</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-emerald-400">{item.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Ideal Customer Section
const IdealCustomerSection = () => {
  const criteria = [
    { icon: Target, text: 'Agencies running paid lead funnels' },
    { icon: Calendar, text: 'Agencies managing appointment volume' },
    { icon: Users, text: 'Agencies with multiple agents' },
    { icon: Eye, text: 'Agencies needing operational visibility' },
  ];

  return (
    <section className="relative py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="glass rounded-xl sm:rounded-2xl p-6 sm:p-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-6 sm:mb-8">
              MissionAssist Works Best For
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {criteria.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/5">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-indigo-400" />
                  </div>
                  <span className="text-sm sm:text-base text-slate-200 text-left">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Target Audience Section
const TargetAudienceSection = () => {
  const audiences = [
    { icon: Building2, title: 'Agency Owners', description: 'Full visibility and data-driven decisions.' },
    { icon: UserCheck, title: 'Operations Managers', description: 'Streamlined workflows and automation.' },
    { icon: Users, title: 'Sales Teams', description: 'AI-powered scripts and automated booking.' },
  ];

  return (
    <section className="relative py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4 sm:mb-6">
            Built for Insurance Agencies
          </h2>
          <p className="text-base sm:text-lg text-slate-300/80">
            Purpose-built for mortgage protection, final expense, Medicare, and life insurance operations.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {audiences.map((audience, i) => (
            <div key={i} className="glass rounded-xl p-6 sm:p-8 text-center hover:bg-white/[0.08] transition-all">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-indigo-500/20 flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <audience.icon className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">{audience.title}</h3>
              <p className="text-slate-400 text-sm">{audience.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Founder Credibility Section
const FounderSection = () => (
  <section className="relative py-16 sm:py-24 overflow-hidden">
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute top-0 right-0 w-[50vw] max-w-[500px] h-[50vw] max-h-[500px] bg-purple-500/10 rounded-full blur-[100px] translate-x-1/4" />
    </div>
    
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="glass rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-center">
          <div className="md:col-span-1 text-center">
            {/* MissionAssist Team Section Logo - 60px inside 120px circle */}
            <div className="w-[120px] h-[120px] rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/30 flex items-center justify-center mx-auto mb-4 border-2 border-white/10">
              <img src={LOGO_COLLAPSED_DATA} alt="MissionAssist" className="h-[60px] w-auto rounded-xl" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-white">MissionAssist Team</h3>
            <p className="text-slate-400 text-sm">Insurance Veterans</p>
          </div>
          
          <div className="md:col-span-2 space-y-3 sm:space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white">
              Built by People Who've Lived It
            </h2>
            <p className="text-sm sm:text-base text-slate-300/80 leading-relaxed">
              MissionAssist was built after years of seeing the same operational problems across insurance agencies. 
              The platform focuses on the systems agencies actually need to manage leads, appointments, and compliance.
            </p>
            <p className="text-sm sm:text-base text-slate-300/80 leading-relaxed">
              We built the platform we wished we had: one that connects every piece of the agency puzzle, 
              from the first lead touch to the final policy close.
            </p>
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2 sm:pt-4">
              <div>
                <div className="text-xl sm:text-2xl font-semibold text-indigo-400">10+</div>
                <div className="text-[10px] sm:text-xs text-slate-500">Years Experience</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-semibold text-indigo-400">50K+</div>
                <div className="text-[10px] sm:text-xs text-slate-500">Leads Processed</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-semibold text-indigo-400">100%</div>
                <div className="text-[10px] sm:text-xs text-slate-500">Insurance Focus</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "What types of insurance agencies is MissionAssist built for?",
      answer: "MissionAssist is purpose-built for agencies running mortgage protection, final expense, Medicare, and life insurance operations. It's especially powerful for agencies using funnel-based lead generation and appointment-based sales processes."
    },
    {
      question: "How does MissionAssist integrate with my existing tools?",
      answer: "MissionAssist integrates seamlessly with GoHighLevel, popular dialers, and CRM systems. Our API allows custom integrations, and our team helps with setup and migration."
    },
    {
      question: "What does the compliance capture feature include?",
      answer: "Our compliance system automatically captures TCPA consent, call recordings, disclosure signatures, and maintains a complete audit trail. Everything is stored securely and easily accessible for compliance reviews."
    },
    {
      question: "How long does it take to get set up?",
      answer: "Most agencies are fully operational within 1-2 weeks. This includes calendar setup, lead source configuration, compliance workflows, and team training. We provide hands-on onboarding support."
    },
    {
      question: "Can I see a demo before committing?",
      answer: "Absolutely! We offer personalized demos where we walk through the platform using scenarios relevant to your specific agency operations. Book a demo to see how MissionAssist fits your workflow."
    }
  ];

  return (
    <section className="relative py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-3 sm:mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-sm sm:text-base text-slate-400">
            Everything you need to know about MissionAssist
          </p>
        </div>
        
        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="glass rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-left hover:bg-white/5 transition-all"
              >
                <span className="font-medium text-white text-sm sm:text-base pr-4">{faq.question}</span>
                <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-400 transition-transform flex-shrink-0 ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-4 sm:px-6 pb-3 sm:pb-4">
                  <p className="text-slate-400 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const CTASection = () => (
  <section className="relative py-20 sm:py-32 overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="relative glass rounded-2xl sm:rounded-3xl p-8 sm:p-12 md:p-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[40vw] max-w-[400px] h-[40vw] max-h-[400px] bg-indigo-500/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[30vw] max-w-[300px] h-[30vw] max-h-[300px] bg-cyan-500/15 rounded-full blur-[80px]" />
        </div>
        
        <div className="relative text-center max-w-2xl mx-auto">
          {/* Hero Card Logo: 56-64px height */}
          <img src={LOGO_COLLAPSED_DATA} alt="MissionAssist" className="h-[60px] w-auto rounded-xl mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4 sm:mb-6">
            Ready to Transform Your Agency?
          </h2>
          <p className="text-base sm:text-lg text-slate-300/80 mb-4">
            Book a demo to see how MissionAssist connects your leads, bookings, compliance, and workflows.
          </p>
          <p className="text-sm text-slate-500 mb-6 sm:mb-10">
            See exactly how MissionAssist fits your agency. 15-minute walkthrough.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <Link
              to="/demo"
              className="inline-flex items-center justify-center gap-2 h-12 sm:h-14 px-8 sm:px-10 rounded-full bg-indigo-500 text-white font-medium hover:bg-indigo-400 transition-all shadow-lg shadow-indigo-500/25 text-base sm:text-lg"
              data-testid="cta-demo-button"
            >
              Book a Demo
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Main HomePage Component
export default function HomePage() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <main data-testid="marketing-home-page">
      <VideoModal isOpen={isVideoOpen} onClose={() => setIsVideoOpen(false)} />
      <HeroSection onVideoOpen={() => setIsVideoOpen(true)} />
      <SocialProofSection />
      <ComparisonSection />
      <AssistEnginesSection />
      <PlatformOverviewSection />
      <ProductScreenshotSection />
      <IdealCustomerSection />
      <TargetAudienceSection />
      <FounderSection />
      <FAQSection />
      <CTASection />
    </main>
  );
}
